import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Vote, CalendarDays, Users, MessagesSquare, Newspaper, HelpCircle, ExternalLink, Landmark, MapPinned, ShieldCheck, PlusCircle, ListChecks, PlayCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { api } from '../lib/api';
import { PageHeader, SectionHeader, Loading, EmptyState, formatDate, RichSection } from '../components/Shared';
import { NextStepBand } from '../components/NextStepBand';
import { NextEventSpotlight } from '../components/election/NextEventSpotlight';
import ElectionEvents from './elections/ElectionEvents';
import KeyDates from './elections/KeyDates';
import SubmitEvent from './elections/SubmitEvent';
import Reminders from './elections/Reminders';

// Primary navigation: the six clear options. Secondary keeps older content
// reachable (nothing was removed).
const SECTIONS = [
  { key: 'overview', label: 'Election Centre', icon: Landmark },
  { key: 'how-to-vote', label: 'How to Vote', icon: Vote },
  { key: 'key-dates', label: 'Key Dates', icon: CalendarDays },
  { key: 'events', label: 'Election Event List', icon: MessagesSquare },
  { key: 'whos-running', label: "Who's Running", icon: Users },
  { key: 'submit-event', label: 'Submit an Event', icon: PlusCircle },
];
const SECONDARY = [
  { key: 'ward-boundaries', label: 'Ward Boundaries', icon: MapPinned },
  { key: 'news', label: 'Election News', icon: Newspaper },
  { key: 'faq', label: 'Voting FAQ', icon: HelpCircle },
  { key: 'links', label: 'Official Links', icon: ExternalLink },
];
// Old URLs keep working: /elections/debates -> events
const ALIASES = { debates: 'events', 'debate-watch': 'events', 'candidate-events': 'events', submit: 'submit-event', 'reminder-emails': 'reminders' };
const HIDDEN_SECTIONS = [{ key: 'reminders', label: 'Weekly Reminder Emails', icon: MessagesSquare }];
const ALL_SECTIONS = [...SECTIONS, ...SECONDARY, ...HIDDEN_SECTIONS];

const CATEGORY_LABELS = {
  official_ckl_update: 'Official CKL Update',
  voting_process: 'Voting Process',
  candidate_update: 'Candidate Update',
  debate_forum: 'Debate / Forum',
  ward_boundary: 'Ward Boundary',
  council_meeting: 'Council Meeting',
  local_news: 'Local News',
  school_board_trustee: 'School Board Trustee',
  steves_statement: "Steve's Statement",
  other: 'Other',
};

const SourceLabel = ({ item }) => {
  const isOfficial = (item.source_name || '').toLowerCase().includes('kawartha lakes') || item.category === 'official_ckl_update';
  const isSteve = item.category === 'steves_statement';
  if (isSteve) return <Badge className="bg-[#1a2744] text-white border-0">Steve's Comment</Badge>;
  if (isOfficial) return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Official Source</Badge>;
  if (!item.source_url) return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Needs Verification</Badge>;
  return <Badge className="bg-sky-100 text-sky-800 border-sky-200">Local News</Badge>;
};

const ResourceCards = ({ items = [], testid }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={testid}>
    {items.map((r, i) => (
      <a key={i} href={r.url} target="_blank" rel="noreferrer">
        <Card className="card-lift h-full"><CardContent className="p-4 flex items-start justify-between gap-2">
          <div>
            <p className="font-semibold text-[#1a2744] text-sm leading-snug">{r.title}</p>
            {r.note && <p className="text-xs text-emerald-700 font-semibold mt-1">{r.note}</p>}
          </div>
          <ExternalLink className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
        </CardContent></Card>
      </a>
    ))}
  </div>
);

const NewsCard = ({ item }) => (
  <Card data-testid="election-news-item">
    <CardContent className="p-5">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <SourceLabel item={item} />
        <Badge variant="outline" className="border-slate-300 text-slate-600">{CATEGORY_LABELS[item.category] || 'Other'}</Badge>
        {item.ward && <Badge variant="outline" className="border-slate-300 text-slate-600">Ward {item.ward}</Badge>}
      </div>
      <p className="font-serif font-bold text-[#1a2744] leading-snug">{item.title}</p>
      <p className="text-xs text-slate-500 mt-1">{formatDate(item.date)}{item.source_name ? <> · Source: {item.source_url ? <a href={item.source_url} target="_blank" rel="noreferrer" className="underline">{item.source_name}</a> : item.source_name}</> : null}{item.candidate_name ? <> · {item.candidate_name}</> : null}</p>
      {item.summary && <p className="text-sm text-slate-700 mt-2 leading-relaxed">{item.summary}</p>}
      {item.why_it_matters && <p className="text-sm text-slate-600 mt-2 leading-relaxed"><strong className="text-[#1a2744]">Why it matters:</strong> {item.why_it_matters}</p>}
      {item.steve_comment && (
        <div className="mt-3 bg-[#f6f4ee] border-l-4 border-[#C9A84C] rounded-r-lg p-3">
          <p className="text-xs font-bold text-[#1a2744] uppercase tracking-wide mb-1">Steve's Comment</p>
          <p className="text-sm text-slate-700 leading-relaxed">{item.steve_comment}</p>
        </div>
      )}
    </CardContent>
  </Card>
);

// Phase 79: strong civic hero for the Election Centre. Admin-editable
// (Admin -> 2026 Election Centre -> Content -> Hero). Internal links use the
// router; http(s) links open in a new tab. No invented dates.
const isExternal = (url) => /^https?:\/\//i.test(url || '');

const HeroButton = ({ btn, primary }) => {
  const cls = primary
    ? 'h-12 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press'
    : 'h-12 border-2 border-[#1a2744]/70 bg-white/70 text-[#1a2744] hover:bg-[#1a2744] hover:text-white font-bold btn-press';
  const testid = `election-hero-btn-${(btn.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
  if (isExternal(btn.url)) {
    return (
      <Button asChild variant={primary ? 'default' : 'outline'} className={cls} data-testid={testid}>
        <a href={btn.url} target="_blank" rel="noopener noreferrer">{btn.label} <ExternalLink className="h-4 w-4 ml-1.5" /></a>
      </Button>
    );
  }
  return (
    <Button asChild variant={primary ? 'default' : 'outline'} className={cls} data-testid={testid}>
      <Link to={btn.url || '/elections'}>{btn.label}</Link>
    </Button>
  );
};

// Default civic imagery: a paper ballot going into a ballot box (light photo)
const ELECTION_HERO_DEFAULT_BG = 'https://images.unsplash.com/photo-1540910419892-4a36d2c3266c?auto=format&fit=crop&w=1600&q=80';

const ElectionHero = ({ hero }) => {
  if (!hero || hero.enabled === false) return null;
  // Light hero treatment (Phase 93): white blend from the LEFT over the image,
  // dark readable text. Admin image (Admin -> 2026 Election Centre) still wins.
  const bgImage = hero.background_image || ELECTION_HERO_DEFAULT_BG;
  return (
    <section className="relative bg-[#f3f7ff] border-b border-slate-200 overflow-hidden" data-testid="election-hero">
      <img src={bgImage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: '75% center' }} />
      <div aria-hidden="true" className="absolute inset-0 hidden sm:block" style={{ background: 'linear-gradient(90deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.88) 38%, rgba(255,255,255,0.42) 68%, rgba(255,255,255,0.06) 100%)' }} />
      <div aria-hidden="true" className="absolute inset-0 sm:hidden" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.9) 55%, rgba(255,255,255,0.65) 100%)' }} />
      <div className="absolute top-0 inset-x-0 h-1 bg-[#1a2744]" aria-hidden="true" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[300px] sm:min-h-[340px] lg:min-h-[380px] py-10 flex flex-col justify-center">
        {hero.eyebrow && <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-[#24406f]" data-testid="election-hero-eyebrow">{hero.eyebrow}</p>}
        <h1 className="mt-2 hero-h1 text-3xl sm:text-4xl lg:text-5xl text-[#0f172a] max-w-2xl whitespace-pre-line" data-testid="election-hero-headline">{hero.headline}</h1>
        {hero.subheadline && <p className="mt-3 text-base sm:text-lg text-slate-700 font-medium leading-relaxed max-w-xl" data-testid="election-hero-subheadline">{hero.subheadline}</p>}
        {/* Reduce hero text: show the body paragraph only when there is no
            subheadline (H1 + one short line + buttons is the standard) */}
        {hero.body && !hero.subheadline && <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed max-w-xl hidden sm:block" data-testid="election-hero-body">{hero.body}</p>}
        {(hero.buttons || []).length > 0 && (
          <div className="mt-6 flex flex-wrap gap-3" data-testid="election-hero-buttons">
            {hero.buttons.map((b, i) => <HeroButton key={b.label || i} btn={b} primary={i === 0} />)}
          </div>
        )}
        {hero.note && (
          <div className="mt-6 flex items-start gap-2.5 rounded-xl bg-white/85 border border-slate-200 p-3.5 max-w-2xl" data-testid="election-hero-note">
            <ShieldCheck className="h-5 w-5 text-[#24406f] shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700 leading-relaxed">{hero.note}</p>
          </div>
        )}
        {hero.source_note && <p className="mt-3 text-xs text-slate-500 max-w-2xl" data-testid="election-hero-source-note">{hero.source_note}</p>}
        {(hero.cards || []).length > 0 && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="election-hero-cards">
            {hero.cards.map((c, i) => {
              const inner = (
                <div className="rounded-xl bg-white/90 hover:bg-white border border-slate-200 p-4 h-full flex items-center justify-between gap-2 transition-colors shadow-sm">
                  <p className="text-sm font-bold text-[#1a2744] leading-snug">{c.label}</p>
                  {isExternal(c.url) ? <ExternalLink className="h-4 w-4 text-[#24406f] shrink-0" /> : <MapPinned className="h-4 w-4 text-[#24406f] shrink-0 hidden" aria-hidden="true" />}
                </div>
              );
              return isExternal(c.url)
                ? <a key={c.label || i} href={c.url} target="_blank" rel="noopener noreferrer" data-testid={`election-hero-card-${i}`}>{inner}</a>
                : <Link key={c.label || i} to={c.url || '/elections'} data-testid={`election-hero-card-${i}`}>{inner}</Link>;
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default function Elections() {
  const { section: rawSection = 'overview' } = useParams();
  const section = ALIASES[rawSection] || rawSection;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [news, setNews] = useState([]);
  const [newsCategory, setNewsCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      api.get('/election'),
      api.get('/election/news'),
    ]).then(([e, n]) => {
      if (e.status === 'fulfilled') setData(e.value.data);
      if (n.status === 'fulfilled') setNews(Array.isArray(n.value.data) ? n.value.data : []);
      setLoading(false);
    });
  }, []);

  const s = data?.settings || {};
  const filteredNews = useMemo(() => news.filter((n) => newsCategory === 'all' || n.category === newsCategory), [news, newsCategory]);
  const active = ALL_SECTIONS.find((x) => x.key === section) || SECTIONS[0];

  // "Who's Running" now lives on its own full directory page
  useEffect(() => {
    if (section === 'whos-running') navigate('/whos-running', { replace: true });
  }, [section, navigate]);

  if (loading) return <Loading />;

  const hero = s.hero;
  const heroVisible = hero && hero.enabled !== false;

  return (
    <div>
      {heroVisible ? (
        <ElectionHero hero={hero} />
      ) : (
        <PageHeader
          pageKey="elections"
          kicker="NEUTRAL, SOURCE-BASED RESOURCE"
          title={s.title || 'Kawartha Lakes 2026 Election Centre'}
          subtitle={s.intro || 'A plain-language resource hub for the 2026 City of Kawartha Lakes municipal election.'}
        />
      )}
      <RichSection id="elections-sections.intro" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10 overflow-x-hidden">
        {/* Section nav — horizontal scroll on mobile */}
        <nav className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap" aria-label="Election Centre sections" data-testid="election-section-nav">
          {SECTIONS.map((x) => (
            <Button
              key={x.key}
              onClick={() => navigate(x.key === 'overview' ? '/elections' : x.key === 'whos-running' ? '/whos-running' : `/elections/${x.key}`)}
              variant={active.key === x.key ? 'default' : 'outline'}
              aria-current={active.key === x.key ? 'page' : undefined}
              className={`h-11 shrink-0 font-bold btn-press ${active.key === x.key ? 'bg-[#1a2744] hover:bg-[#243456] text-white' : 'border-slate-300 text-[#1a2744] bg-white'}`}
              data-testid={`election-nav-${x.key}`}
            >
              <x.icon className="h-4 w-4 mr-1.5" /> {x.label}
            </Button>
          ))}
        </nav>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" aria-label="More Election Centre pages" data-testid="election-secondary-nav">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">More:</span>
          {SECONDARY.map((x) => (
            <button key={x.key} type="button" onClick={() => navigate(`/elections/${x.key}`)} className={`inline-flex items-center gap-1 underline-offset-4 hover:underline ${active.key === x.key ? 'font-bold text-[#1a2744] underline' : 'text-slate-600'}`} data-testid={`election-nav-${x.key}`}>
              <x.icon className="h-3.5 w-3.5" /> {x.label}
            </button>
          ))}
          <Link to="/videos" className="inline-flex items-center gap-1 text-slate-600 underline-offset-4 hover:underline" data-testid="election-nav-videos"><PlayCircle className="h-3.5 w-3.5" /> Video Library</Link>
        </div>

        <div className="mt-6">
          {/* ---------------- OVERVIEW ---------------- */}
          {active.key === 'overview' && (
            <div className="space-y-8" data-testid="election-overview">
              <NextEventSpotlight />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="election-quick-links">
                {[
                  { to: '/elections/events', icon: ListChecks, label: 'Election Event List', sub: 'All CKL candidate events', testid: 'election-quick-events' },
                  { to: '/elections/key-dates', icon: CalendarDays, label: 'Key Dates', sub: 'Official City dates', testid: 'election-quick-key-dates' },
                  { to: '/elections/how-to-vote', icon: Vote, label: 'How to Vote', sub: 'PIN letters, methods, help', testid: 'election-quick-how-to-vote' },
                  { to: '/whos-running', icon: Users, label: "Who's Running", sub: 'Mayor, wards, trustees', testid: 'election-quick-whos-running' },
                ].map((q) => (
                  <Link key={q.to} to={q.to} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4 hover:border-[#C9A84C] card-lift" data-testid={q.testid}>
                    <span className="h-10 w-10 rounded-lg bg-[#1a2744] flex items-center justify-center shrink-0"><q.icon className="h-5 w-5 text-white" /></span>
                    <span><span className="block font-bold text-[#1a2744] text-sm leading-snug">{q.label}</span><span className="block text-xs text-slate-500">{q.sub}</span></span>
                  </Link>
                ))}
              </div>
              <Card className="bg-[#1a2744] border-0"><CardContent className="p-6 sm:p-8">
                <p className="text-xs font-semibold uppercase tracking-widest text-[#C9A84C] mb-2">ABOUT THE 2026 MUNICIPAL ELECTION</p>
                <p className="text-white text-lg leading-relaxed max-w-3xl" data-testid="election-about-text">{s.about}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Official CKL Source</Badge>
                  <a href={s.official_url || data?.official_source_url} target="_blank" rel="noreferrer" className="text-sm text-slate-300 underline">City of Kawartha Lakes Elections</a>
                </div>
              </CardContent></Card>

              <div>
                <SectionHeader kicker="CANDIDATES" title="Who's Running in the 2026 Kawartha Lakes Municipal Election" dek="A source-based directory built from the official CKL candidates list: Mayor, all 8 ward Councillor races, school board trustees, and withdrawals." />
                <Card className="bg-[#1a2744] border-0" data-testid="election-overview-whos-running">
                  <CardContent className="p-6">
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#C9A84C] mb-2">UNOFFICIAL LIST OF CANDIDATES FOR THE 2026 MUNICIPAL ELECTION</p>
                    <p className="text-slate-200 text-sm leading-relaxed max-w-3xl">Candidates are unofficial until certified by the City Clerk after nominations close. Every listing shows its official source and last-checked date.</p>
                    <div className="mt-4 flex flex-wrap gap-3">
                      <Button asChild className="h-11 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="election-overview-whos-running-btn"><Link to="/whos-running"><Users className="h-4 w-4 mr-1.5" /> View Who's Running</Link></Button>
                      <Button asChild variant="outline" className="h-11 border-white bg-transparent text-white hover:bg-white/10 font-bold btn-press"><a href="https://www.kawarthalakes.ca/government-administration/elections/candidates-list/" target="_blank" rel="noreferrer">Official CKL Candidates List <ExternalLink className="h-3.5 w-3.5 ml-1.5" /></a></Button>
                    </div>
                  </CardContent>
                </Card>
                <Card className="mt-4 border-[#C9A84C]/60 bg-[#FBF7EE]" data-testid="election-overview-find-ward">
                  <CardContent className="p-6">
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#8a6d2c] mb-1.5">FEATURED</p>
                    <p className="font-serif font-bold text-xl text-[#1a2744]">Find Your Ward</p>
                    <p className="text-sm text-slate-700 mt-1.5 leading-relaxed max-w-2xl">See the ward boundary map and quickly find who is running in your area.</p>
                    <Button asChild className="h-11 mt-4 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="election-overview-find-ward-btn"><Link to="/whos-running#find-your-ward"><MapPinned className="h-4 w-4 mr-1.5" /> View Ward Map</Link></Button>
                  </CardContent>
                </Card>
              </div>

              <div>
                <SectionHeader kicker="VOTER RESOURCES" title="Resources for voters" dek="Official City of Kawartha Lakes links. Always confirm details at the source." />
                <ResourceCards items={s.voter_resources || []} testid="voter-resource-cards" />
              </div>

              <div>
                <SectionHeader kicker="CANDIDATE RESOURCES" title="Resources for candidates" />
                <ResourceCards items={s.candidate_resources || []} testid="candidate-resource-cards" />
              </div>

              <div>
                <SectionHeader kicker="SCHOOL BOARD" title="School Board Trustee information" />
                <ResourceCards items={s.school_board || []} testid="school-board-cards" />
              </div>

              <div>
                <SectionHeader kicker="WARD BOUNDARIES" title="Find your ward" />
                <Card><CardContent className="p-5">
                  <p className="text-sm text-slate-700 leading-relaxed">The City of Kawartha Lakes has 8 wards represented by 8 Councillors plus the Mayor. One Councillor is elected per ward.</p>
                  <p className="text-sm text-slate-600 mt-2"><strong className="text-[#1a2744]">Ward 8:</strong> {s.ward_boundary_summary}</p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Button asChild className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="election-ward-map-btn"><Link to="/map"><MapPinned className="h-4 w-4 mr-1.5" /> View the Ward Boundary Map</Link></Button>
                    <Button asChild variant="outline" className="h-11 border-[#1a2744] text-[#1a2744] font-bold btn-press"><a href="https://www.kawarthalakes.ca/government-administration/elections/ward-boundaries/" target="_blank" rel="noreferrer">Official Ward Boundaries <ExternalLink className="h-3.5 w-3.5 ml-1.5" /></a></Button>
                  </div>
                </CardContent></Card>
              </div>
            </div>
          )}

          {/* ---------------- HOW TO VOTE ---------------- */}
          {active.key === 'how-to-vote' && (
            <div className="space-y-6" data-testid="election-how-to-vote">
              <SectionHeader kicker="PLAIN LANGUAGE" title="How to vote in 2026" dek="Neutral, source-based process information. Always confirm with the official CKL elections page." />
              <Card><CardContent className="p-5 space-y-3 text-sm text-slate-700 leading-relaxed">
                <p><strong className="text-[#1a2744]">1. Check that you are on the voters' list.</strong> Use the official "Am I on the Voters' List?" tool from the City.</p>
                <p><strong className="text-[#1a2744]">2. Confirm your ward.</strong> The city has 8 wards. Use the official ward boundary map or this site's <Link to="/map" className="underline font-semibold">ward map</Link>.</p>
                <p><strong className="text-[#1a2744]">3. Learn the voting methods.</strong> The City provides information on electronic voting and other methods on its official elections page.</p>
                <p><strong className="text-[#1a2744]">4. Vote by October 26, 2026.</strong> The next Municipal Election is scheduled for October 26, 2026.</p>
                <p className="text-xs text-slate-500">Source: City of Kawartha Lakes, Office of the City Clerk. <a href={s.official_url} target="_blank" rel="noreferrer" className="underline">Official elections page</a>.</p>
              </CardContent></Card>
              <ResourceCards items={(s.voter_resources || []).filter((r) => ['Register to Vote', "Am I on the Voters' List?", 'Electronic Voting', 'Information for Voters'].includes(r.title))} testid="how-to-vote-cards" />
            </div>
          )}

          {/* ---------------- KEY DATES (source-backed, daily re-checked) ---------------- */}
          {active.key === 'key-dates' && <KeyDates />}

          {/* ---------------- WHO'S RUNNING (redirects to the full directory page) ---------------- */}
          {active.key === 'whos-running' && (
            <div className="space-y-6" data-testid="election-whos-running">
              <SectionHeader kicker="CANDIDATES" title="Who's running in 2026" dek="The full source-based candidate directory has moved to its own page." />
              <Button asChild className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="election-whos-running-link">
                <Link to="/whos-running"><Users className="h-4 w-4 mr-1.5" /> Open Who's Running in 2026</Link>
              </Button>
            </div>
          )}

          {/* ---------------- WARD BOUNDARIES ---------------- */}
          {active.key === 'ward-boundaries' && (
            <div className="space-y-5" data-testid="election-ward-boundaries">
              <SectionHeader kicker="FIND YOUR WARD" title="Kawartha Lakes ward boundaries" dek="The City of Kawartha Lakes has 8 wards represented by 8 Councillors plus the Mayor. One Councillor per ward." />
              <Card><CardContent className="p-5">
                <p className="text-sm text-slate-700 leading-relaxed"><strong className="text-[#1a2744]">Ward 8:</strong> {s.ward_boundary_summary}</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button asChild className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="ward-boundaries-map-btn"><Link to="/map"><MapPinned className="h-4 w-4 mr-1.5" /> Open the Interactive Ward Map</Link></Button>
                  <Button asChild variant="outline" className="h-12 border-[#1a2744] text-[#1a2744] font-bold btn-press" data-testid="ward-boundaries-official-btn"><a href="https://www.kawarthalakes.ca/government-administration/elections/ward-boundaries/" target="_blank" rel="noreferrer">Official Ward Boundary Map <ExternalLink className="h-3.5 w-3.5 ml-1.5" /></a></Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-3">Ward boundaries. Source: <a href="https://www.kawarthalakes.ca/government-administration/elections/ward-boundaries/" target="_blank" rel="noreferrer" className="underline">City of Kawartha Lakes Ward Boundaries</a></p>
              </CardContent></Card>
              <Card><CardContent className="p-5">
                <p className="font-serif font-bold text-[#1a2744] mb-2">On the interactive map you can:</p>
                <ul className="space-y-1.5 text-sm text-slate-700">
                  <li>• View all 8 official ward boundaries with labels</li>
                  <li>• Tap any ward to see its boundary description and public issue counts</li>
                  <li>• Switch between Ward 8 focus and the full Kawartha Lakes picture</li>
                </ul>
              </CardContent></Card>
            </div>
          )}

          {/* ---------------- DEBATES & CANDIDATE EVENTS (all of CKL) ---------------- */}
          {active.key === 'events' && <ElectionEvents />}

          {/* ---------------- SUBMIT AN EVENT ---------------- */}
          {active.key === 'submit-event' && <SubmitEvent />}

          {/* ---------------- WEEKLY REMINDER EMAILS ---------------- */}
          {active.key === 'reminders' && <Reminders />}

          {/* ---------------- ELECTION NEWS ---------------- */}
          {active.key === 'news' && (
            <div data-testid="election-news">
              <SectionHeader kicker="SOURCE-BASED" title="Election news and updates" dek="Every item links to its source. Official information and Steve's commentary are always kept separate." />
              <div className="mb-4 max-w-xs">
                <Select value={newsCategory} onValueChange={setNewsCategory}>
                  <SelectTrigger className="h-11 bg-white" data-testid="election-news-category-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {Object.entries(CATEGORY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {filteredNews.length === 0 ? (
                <EmptyState title="No published election news yet" body="Election news is imported draft-first from official and local sources, then reviewed before publishing." />
              ) : (
                <div className="space-y-3">{filteredNews.map((n) => <NewsCard key={n.id} item={n} />)}</div>
              )}
            </div>
          )}

          {/* ---------------- FAQ ---------------- */}
          {active.key === 'faq' && (
            <div data-testid="election-faq">
              <SectionHeader kicker="PLAIN LANGUAGE" title="Voting FAQ" dek="Common questions with source-based answers." />
              {(s.faq || []).length === 0 ? (
                <EmptyState title="No FAQ published yet" body="Frequently asked questions will appear here." />
              ) : (
                <Accordion type="single" collapsible className="bg-white rounded-2xl border border-slate-200 px-5">
                  {(s.faq || []).map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className={i === (s.faq || []).length - 1 ? 'border-0' : ''}>
                      <AccordionTrigger className="text-left font-bold text-[#1a2744] hover:no-underline" data-testid={`faq-question-${i}`}>{f.q}</AccordionTrigger>
                      <AccordionContent className="text-slate-700 leading-relaxed">
                        {f.a}
                        {f.source_url && <> <a href={f.source_url} target="_blank" rel="noreferrer" className="underline text-sm">Official source</a></>}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          )}

          {/* ---------------- OFFICIAL LINKS ---------------- */}
          {active.key === 'links' && (
            <div data-testid="election-links">
              <SectionHeader kicker="GO TO THE SOURCE" title="Official election links" dek="Always rely on official sources for election rules, dates, and candidate information." />
              <ResourceCards items={(s.official_links || []).map((l) => ({ ...l, note: 'Official Source' }))} testid="official-links-cards" />
            </div>
          )}
        </div>

        {/* Neutrality disclaimer */}
        <p className="mt-10 text-xs text-slate-500 leading-relaxed max-w-3xl" data-testid="election-disclaimer">
          <ShieldCheck className="inline h-3.5 w-3.5 mr-1 text-slate-400" />
          Election process information should be verified with official City of Kawartha Lakes election sources. Candidate, debate, and news items are source-based and may be updated as new information becomes available.
          {' '}This Election Centre is a resource provided by the Steve Lesperance campaign. It is not an official City of Kawartha Lakes website. Confirm details with the{' '}
          <a href={s.official_url || 'https://www.kawarthalakes.ca/government-administration/elections/'} target="_blank" rel="noreferrer" className="underline">official City of Kawartha Lakes elections page</a>.
        </p>
      </div>

      {/* One clear next step */}
      <NextStepBand
        title={active.key === 'events' || active.key === 'submit-event' ? 'Not sure which ward you are in?' : 'Do not miss a debate or meet and greet near you.'}
        buttonLabel={active.key === 'events' || active.key === 'submit-event' ? 'Find Your Ward' : 'Election Event List'}
        to={active.key === 'events' || active.key === 'submit-event' ? '/find-your-ward' : '/elections/events'}
        secondaryLabel={active.key === 'events' || active.key === 'submit-event' ? 'Or see who is running' : 'Or submit an event'}
        secondaryTo={active.key === 'events' || active.key === 'submit-event' ? '/whos-running' : '/elections/submit-event'}
        testId="elections-next-step"
      />
    </div>
  );
}
